/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  AttachFile,
  Check,
  Description,
  DoneAll,
  EmojiEmotions,
  Mic,
  Refresh,
  Schedule,
  Send,
  Stop,
  WhatsApp,
} from "@mui/icons-material";
import EmojiPickerReact, { EmojiStyle, Theme as EmojiTheme } from "emoji-picker-react";
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from "date-fns";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import { useWebSocket } from "context/WebSocketContext";
import {
  getApiErrorMessage,
  getSeatNotAssignedMessage,
  isSeatNotAssignedError,
} from "utils/apiErrors";
import { formatWhatsAppLanguage } from "components/SettingsComponents/WhatsAppTemplatesSettings";
import {
  APP_SCALE,
  APP_SCALE_MIN_WIDTH,
  scaledVh,
} from "assets/theme/base/appScale";
import { wrapPaperPositioning } from "assets/theme/base/cssZoomOverlays";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { v2 as t } from "styles/tokens/v2";

const WA_GREEN = "#25D366";
const WA_GREEN_DARK = "#128C7E";
// Header uses theme brand light blue; selected thread stays WhatsApp green.
const HEADER_BLUE = "#1565C8";
const HEADER_BLUE_BG = "#D6E3FF";
const SELECT_GREEN = "#25D366";
const SELECT_GREEN_BG = "rgba(37, 211, 102, 0.18)";
const SELECT_GREEN_BG_HOVER = "rgba(37, 211, 102, 0.26)";
const CHAT_BG = "#f4f6f8";
const OUTBOUND_BG = "#dcf8c6";
const INBOUND_BG = "#ffffff";

// Thin, near-invisible scrollbar so the chat does not show the browser's
// default wide bar over the message pane.
const THIN_SCROLLBAR = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0,0,0,0.18) transparent",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.16)",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "rgba(0,0,0,0.28)" },
};


function previewTemplateBody(body, values) {
  return (body || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n) => {
    const value = values[Number(n) - 1];
    return value ? value : match;
  });
}

/**
 * Lock the document while the inbox is open and size the panel to the space
 * still visible below the navbar. Without the lock the dashboard shell keeps
 * its own scrollbar; only the message pane should scroll.
 *
 * body { zoom: APP_SCALE } above 992px: getBoundingClientRect / visualViewport
 * are visual px, but `height: Npx` is CSS px that zoom then shrinks. Remaining
 * visual space must be divided by the effective scale. DashboardLayout adds
 * p:3 below this box — leave that padding out of the height or the composer
 * is clipped under the fold.
 */
function useFitViewportHeight() {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const remainingVisual = viewportBottom - rect.top;
      if (remainingVisual <= 0) return;

      // Visual px per CSS px. Prefer a live reading so browser zoom and the
      // 80% app scale stay in sync; fall back to APP_SCALE before first layout.
      const measuredScale =
        el.offsetHeight > 0 && rect.height > 0
          ? rect.height / el.offsetHeight
          : window.innerWidth >= APP_SCALE_MIN_WIDTH
            ? APP_SCALE
            : 1;
      const scale = measuredScale || 1;
      // DashboardLayout wraps the page in p: 3 (24px). That padding sits below
      // this box and is already in CSS pixels. Extra 8px keeps the send bar
      // above the fold when rounding or the Windows taskbar clips the window.
      const layoutPadCss = 32;
      const next = Math.max(280, Math.floor(remainingVisual / scale - layoutPadCss));
      setHeight((prev) => (prev === next ? prev : next));
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const settle = setTimeout(measure, 300);
    window.addEventListener("resize", measure);
    vvSafeOn(window.visualViewport, "resize", measure);
    vvSafeOn(window.visualViewport, "scroll", measure);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(body);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      window.removeEventListener("resize", measure);
      vvSafeOff(window.visualViewport, "resize", measure);
      vvSafeOff(window.visualViewport, "scroll", measure);
      observer?.disconnect();
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  return [ref, height];
}

function vvSafeOn(vv, event, fn) {
  vv?.addEventListener(event, fn);
}

function vvSafeOff(vv, event, fn) {
  vv?.removeEventListener(event, fn);
}

function EmojiPicker({ anchorEl, onClose, onPick }) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      // body { zoom } misplaces portaled Popovers; re-sync paper top/left after open.
      TransitionProps={{
        onEntering: (node) => {
          const paper = node?.classList?.contains("MuiPopover-paper")
            ? node
            : node?.querySelector?.(".MuiPopover-paper");
          if (paper) wrapPaperPositioning(paper);
        },
        onEntered: (node) => {
          const paper = node?.classList?.contains("MuiPopover-paper")
            ? node
            : node?.querySelector?.(".MuiPopover-paper");
          if (paper) wrapPaperPositioning(paper);
        },
      }}
      PaperProps={{
        sx: {
          // The app theme renders every Popover paper transparent, which made
          // the picker unreadable over the chat. Force an opaque surface.
          p: 0,
          backgroundColor: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      <EmojiPickerReact
        onEmojiClick={(emojiData) => onPick(emojiData.emoji)}
        theme={EmojiTheme.LIGHT}
        emojiStyle={EmojiStyle.NATIVE}
        width={340}
        height={420}
        searchPlaceholder="Search emoji"
        previewConfig={{ showPreview: false }}
        skinTonesDisabled
        lazyLoadEmojis
      />
    </Popover>
  );
}

function assignmentChip(assignment) {
  const map = {
    waiting: { label: "Waiting", color: "warning" },
    queued: { label: "Queued", color: "default" },
    mine: { label: "Mine", color: "success" },
  };
  const cfg = map[assignment];
  if (!cfg) return null;
  return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" sx={{ height: 22 }} />;
}

/** Related records open in their own tab so the thread stays put. */
function openInNewTab(path) {
  window.open(path, "_blank", "noopener,noreferrer");
}

function relatedPath(item) {
  if (!item?.object_type || !item?.id) return null;
  const map = {
    lead: `/leads/${item.id}`,
    account: `/accounts/${item.id}`,
    case: `/cases/${item.id}`,
    contact: `/contacts/${item.id}`,
  };
  return map[item.object_type] || null;
}

function RelatedChips({ related, onOpen, compact = false, inline = false }) {
  if (!related?.length) return null;
  return (
    <Box
      display="flex"
      gap={0.5}
      flexWrap="wrap"
      mt={inline ? 0 : compact ? 0.5 : 0.75}
    >
      {related.map((item) => {
        const path = relatedPath(item);
        const typeLabel = (item.object_type || "").replace(/^./, (c) => c.toUpperCase());
        return (
          <Chip
            key={`${item.object_type}-${item.id}`}
            size="small"
            color="success"
            variant="outlined"
            label={`${typeLabel}: ${item.label}`}
            // Stop row selection when opening a related CRM record.
            onClick={
              path
                ? (event) => {
                    event.stopPropagation();
                    onOpen(path);
                  }
                : undefined
            }
            sx={{ cursor: path ? "pointer" : "default", height: compact ? 20 : 24, fontSize: "0.7rem" }}
          />
        );
      })}
    </Box>
  );
}

function formatDayLabel(dateStr) {
  if (!dateStr) return "";
  const d = typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "d MMMM yyyy");
}

function formatMessageTime(dateStr) {
  if (!dateStr) return "";
  const d = typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
  return format(d, "HH:mm");
}

function DeliveryIcon({ status }) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === "read") {
    return <DoneAll sx={{ fontSize: 14, color: "#53bdeb", ml: 0.5 }} />;
  }
  if (s === "delivered") {
    return <DoneAll sx={{ fontSize: 14, color: "text.secondary", ml: 0.5 }} />;
  }
  if (s === "sent") {
    return <Check sx={{ fontSize: 14, color: "text.secondary", ml: 0.5 }} />;
  }
  if (s === "pending" || s === "queued") {
    return <Schedule sx={{ fontSize: 14, color: "text.secondary", ml: 0.5 }} />;
  }
  return null;
}

function DateDivider({ label }) {
  return (
    <Box display="flex" justifyContent="center" my={2}>
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: "rgba(255,255,255,0.85)",
          fontSize: "0.7rem",
          fontWeight: 500,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      />
    </Box>
  );
}

function ContactAvatar({ size = 40 }) {
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: WA_GREEN }}>
      <WhatsApp sx={{ color: "#fff", fontSize: size * 0.58 }} />
    </Avatar>
  );
}

function ThreadBubble({ message }) {
  const isOutbound = message.direction === "outbound";
  const media = message.media_json || null;
  const kind = media?.kind || null;
  const [audioBroken, setAudioBroken] = useState(false);
  const audioUrl = kind === "audio" && !audioBroken ? media?.url : null;
  const mediaLabel =
    kind === "audio"
      ? "Voice message"
      : kind === "image"
        ? "Image"
        : kind === "video"
          ? "Video"
          : kind === "document"
            ? media?.filename || "Document"
            : null;

  return (
    <Box display="flex" justifyContent={isOutbound ? "flex-end" : "flex-start"} mb={0.75} px={0.5}>
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "72%" },
          px: 1.5,
          py: 1,
          borderRadius: isOutbound ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
          bgcolor: isOutbound ? OUTBOUND_BG : INBOUND_BG,
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
        }}
      >
        {!isOutbound && message.sender_name ? (
          <Typography variant="caption" fontWeight="bold" color={WA_GREEN_DARK} display="block" mb={0.25}>
            {message.sender_name}
          </Typography>
        ) : null}
        {audioUrl ? (
          <Box sx={{ minWidth: { xs: 200, sm: 240 }, maxWidth: "100%", lineHeight: 0 }}>
            <audio
              controls
              preload="metadata"
              src={audioUrl}
              onError={() => setAudioBroken(true)}
              style={{ width: "100%", height: 36, display: "block" }}
            >
              Voice message
            </audio>
          </Box>
        ) : mediaLabel ? (
          <Box display="flex" alignItems="center" gap={0.75} mb={message.body && !String(message.body).startsWith("[") ? 0.5 : 0}>
            {kind === "audio" ? (
              <Mic sx={{ fontSize: 18, color: WA_GREEN_DARK }} />
            ) : (
              <AttachFile sx={{ fontSize: 18, color: WA_GREEN_DARK }} />
            )}
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {mediaLabel}
            </Typography>
          </Box>
        ) : null}
        {message.body && !(mediaLabel && String(message.body).startsWith("[")) ? (
          <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>
            {message.body}
          </Typography>
        ) : null}
        {!mediaLabel && !message.body ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            (empty)
          </Typography>
        ) : null}
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={0.25} gap={0.25}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
            {formatMessageTime(message.sent_at)}
          </Typography>
          {isOutbound ? <DeliveryIcon status={message.status} /> : null}
        </Box>
      </Box>
    </Box>
  );
}

function MessageThread({ messages }) {
  const grouped = useMemo(() => {
    const items = [];
    let lastDay = null;
    (messages || []).forEach((msg) => {
      const day = msg.sent_at ? formatDayLabel(msg.sent_at) : "";
      if (day && day !== lastDay) {
        items.push({ type: "divider", key: `d-${day}-${msg.id}`, label: day });
        lastDay = day;
      }
      items.push({ type: "message", key: msg.id, message: msg });
    });
    return items;
  }, [messages]);

  return (
    <>
      {grouped.map((item) =>
        item.type === "divider" ? (
          <DateDivider key={item.key} label={item.label} />
        ) : (
          <ThreadBubble key={item.key} message={item.message} />
        )
      )}
    </>
  );
}

function InboxRow({ item, selected, onSelect }) {
  const hasUnread = item.unread_count > 0;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item.conversation_id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(item.conversation_id);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        py: 1.5,
        px: 2,
        cursor: "pointer",
        borderBottom: "1px solid",
        borderColor: "divider",
        borderLeft: selected ? `4px solid ${SELECT_GREEN}` : "4px solid transparent",
        // Theme ListItem selected styles were washing this out — set background
        // on a plain Box so the WhatsApp green highlight always shows.
        backgroundColor: selected
          ? SELECT_GREEN_BG
          : hasUnread
            ? "rgba(37, 211, 102, 0.06)"
            : "transparent",
        "&:hover": {
          backgroundColor: selected ? SELECT_GREEN_BG_HOVER : "rgba(37, 211, 102, 0.05)",
        },
      }}
    >
      <Badge
        color="error"
        badgeContent={item.unread_count}
        invisible={!hasUnread}
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mr: 1.5, mt: 0.5 }}
      >
        <ContactAvatar size={44} />
      </Badge>
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography
            variant="subtitle2"
            fontWeight={hasUnread || selected ? 700 : 600}
            noWrap
            sx={{ maxWidth: "100%", color: selected ? WA_GREEN_DARK : "inherit" }}
          >
            {item.contact_name || item.phone_number || "Unknown"}
          </Typography>
          {assignmentChip(item.assignment)}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {item.phone_number}
        </Typography>
        <Typography
          variant="body2"
          color={hasUnread ? "text.primary" : "text.secondary"}
          fontWeight={hasUnread ? 500 : 400}
          sx={{
            mt: 0.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.35,
          }}
        >
          {item.last_message_preview || "No messages yet"}
        </Typography>
        {item.last_message_at ? (
          <Typography
            variant="caption"
            display="block"
            mt={0.25}
            color={hasUnread || selected ? WA_GREEN_DARK : "text.secondary"}
            fontWeight={hasUnread || selected ? 600 : 400}
          >
            {`Last message: ${formatDistanceToNow(new Date(item.last_message_at), {
              addSuffix: false,
            })}`}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { axiosInstance } = useAuth();
  const { whatsappInboxEvent, wsConnected } = useWebSocket();

  const selectedId = searchParams.get("conversation");
  const [inbox, setInbox] = useState([]);
  const [thread, setThread] = useState(null);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferCandidates, setTransferCandidates] = useState([]);
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [transferringTo, setTransferringTo] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [inboxError, setInboxError] = useState(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVars, setTemplateVars] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [sendingMedia, setSendingMedia] = useState(false);
  const messagesScrollRef = useRef(null);
  const replyInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const plusBtnRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const recordingConversationIdRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  const openedScrollConversationRef = useRef(null);
  const [pageRef, pageHeight] = useFitViewportHeight();

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const lastMessageId =
    thread?.messages?.length > 0
      ? thread.messages[thread.messages.length - 1]?.id
      : null;

  const loadInbox = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoadingInbox(true);
    try {
      const res = await axiosInstance.get("/whatsapp/inbox");
      setInbox(Array.isArray(res.data) ? res.data : []);
      setInboxError(null);
    } catch (error) {
      if (!quiet) {
        // detail is often an object (seat / entitlement). Rendering it crashes the page.
        // Queue membership alone does not unlock inbox — agents need a WHATSAPP seat.
        setInboxError(
          isSeatNotAssignedError(error)
            ? getSeatNotAssignedMessage(error, "Could not load messages.")
            : getApiErrorMessage(error, "Could not load messages."),
        );
        setInbox([]);
      }
    } finally {
      if (!quiet) setLoadingInbox(false);
    }
  }, [axiosInstance]);

  const loadThread = useCallback(
    async (conversationId, { quiet = false } = {}) => {
      if (!conversationId) {
        setThread(null);
        return;
      }
      if (!quiet) setLoadingThread(true);
      try {
        const res = await axiosInstance.get(`/whatsapp/conversations/${conversationId}`);
        setThread(res.data);
        if (res.data?.can_reply) {
          try {
            await axiosInstance.post(`/whatsapp/conversations/${conversationId}/read`);
          } catch {
            /* unread badge can lag; thread still usable */
          }
        }
        await loadInbox({ quiet: true });
      } catch (error) {
        if (!quiet) {
          setToast({
            severity: "error",
            message: getApiErrorMessage(error, "Could not load conversation."),
          });
        }
      } finally {
        if (!quiet) setLoadingThread(false);
      }
    },
    [axiosInstance, loadInbox]
  );

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInbox({ quiet: true });
      if (selectedId) await loadThread(selectedId, { quiet: true });
    } finally {
      setRefreshing(false);
    }
  };

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/whatsapp/message-templates");
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTemplates([]);
    }
  }, [axiosInstance]);

  useEffect(() => {
    loadInbox();
    loadTemplates();
  }, [loadInbox, loadTemplates]);

  useEffect(() => {
    loadThread(selectedId);
  }, [selectedId, loadThread]);

  useEffect(() => {
    const pollMs = wsConnected ? 30000 : 5000;
    const interval = setInterval(() => {
      loadInbox({ quiet: true });
      if (selectedId) loadThread(selectedId, { quiet: true });
    }, pollMs);
    return () => clearInterval(interval);
  }, [loadInbox, loadThread, selectedId, wsConnected]);

  useEffect(() => {
    if (!whatsappInboxEvent) return;
    loadInbox({ quiet: true });
    if (selectedId) loadThread(selectedId, { quiet: true });
  }, [whatsappInboxEvent, loadInbox, loadThread, selectedId]);

  const scrollThreadToLatest = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return false;
    el.scrollTop = el.scrollHeight;
    return true;
  }, []);

  // Jump to the newest message whenever a thread is opened. Do this after the
  // matching conversation is on screen — selectedId changes first, while the
  // previous thread (or the loading spinner) is still mounted, and a too-early
  // scroll would mark the switch as done and leave the new pane at the top.
  useLayoutEffect(() => {
    const threadId = thread?.conversation_id != null ? String(thread.conversation_id) : null;
    if (!selectedId || !lastMessageId || loadingThread || threadId !== String(selectedId)) {
      return undefined;
    }
    if (openedScrollConversationRef.current === selectedId) return undefined;

    const jump = () => {
      if (scrollThreadToLatest()) {
        openedScrollConversationRef.current = selectedId;
      }
    };
    jump();
    const raf = requestAnimationFrame(jump);
    const later = setTimeout(jump, 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(later);
    };
  }, [selectedId, lastMessageId, loadingThread, thread?.conversation_id, scrollThreadToLatest]);

  // Live updates: stay pinned only if the agent was already near the bottom.
  useEffect(() => {
    if (openedScrollConversationRef.current !== selectedId) return;
    const el = messagesScrollRef.current;
    if (!el || lastMessageId == null) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 140) {
      scrollThreadToLatest();
    }
  }, [lastMessageId, selectedId, scrollThreadToLatest]);

  const handleClaim = async () => {
    if (!selectedId) return;
    setClaiming(true);
    try {
      await axiosInstance.post(`/whatsapp/conversations/${selectedId}/claim`);
      setToast({ severity: "success", message: "Conversation claimed." });
      await loadThread(selectedId);
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not claim conversation."),
      });
    } finally {
      setClaiming(false);
    }
  };

  const handleEndSession = async () => {
    if (!selectedId) return;
    setEndingSession(true);
    try {
      await axiosInstance.post(`/whatsapp/conversations/${selectedId}/end-session`);
      setToast({
        severity: "success",
        message:
          "Session ended and unassigned. The next customer message will route to the next available agent in the queue.",
      });
      setSearchParams({});
      setThread(null);
      await loadInbox({ quiet: true });
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not end session."),
      });
    } finally {
      setEndingSession(false);
    }
  };

  const openTransferDialog = async () => {
    if (!selectedId) return;
    setTransferOpen(true);
    setLoadingTransfer(true);
    setTransferCandidates([]);
    try {
      const res = await axiosInstance.get(
        `/whatsapp/conversations/${selectedId}/transfer-candidates`
      );
      setTransferCandidates(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not load agents."),
      });
      setTransferOpen(false);
    } finally {
      setLoadingTransfer(false);
    }
  };

  const handleTransfer = async (userId) => {
    if (!selectedId || !userId) return;
    setTransferringTo(userId);
    try {
      await axiosInstance.post(`/whatsapp/conversations/${selectedId}/transfer`, {
        user_id: userId,
      });
      setToast({ severity: "success", message: "Conversation assigned to the other agent." });
      setTransferOpen(false);
      setSearchParams({});
      setThread(null);
      await loadInbox({ quiet: true });
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not assign conversation."),
      });
    } finally {
      setTransferringTo(null);
    }
  };

  const handleEmojiPick = (emoji) => {
    const input = replyInputRef.current;
    const caret = input?.selectionStart;
    setReplyText((prev) =>
      typeof caret === "number"
        ? `${prev.slice(0, caret)}${emoji}${prev.slice(caret)}`
        : `${prev}${emoji}`
    );
    setEmojiAnchor(null);
    // Return focus after the popover unmounts so typing continues in place.
    requestAnimationFrame(() => {
      input?.focus();
      if (typeof caret === "number") {
        const next = caret + emoji.length;
        input?.setSelectionRange?.(next, next);
      }
    });
  };

  const appendOutboundMessage = useCallback((message, conversationId) => {
    if (!message) return;
    setThread((prev) => {
      if (!prev) return prev;
      if (
        conversationId != null &&
        String(prev.conversation_id || "") !== String(conversationId)
      ) {
        return prev;
      }
      const existing = prev.messages || [];
      if (message.id != null && existing.some((m) => String(m.id) === String(message.id))) {
        return prev;
      }
      return { ...prev, messages: [...existing, message] };
    });
  }, []);

  const replaceTempOutboundMessage = useCallback((tempId, message, conversationId) => {
    setThread((prev) => {
      if (!prev) return prev;
      if (
        conversationId != null &&
        String(prev.conversation_id || "") !== String(conversationId)
      ) {
        return prev;
      }
      const existing = prev.messages || [];
      const withoutTemp = existing.filter((m) => String(m.id) !== String(tempId));
      if (message?.id != null && withoutTemp.some((m) => String(m.id) === String(message.id))) {
        return { ...prev, messages: withoutTemp };
      }
      return { ...prev, messages: message ? [...withoutTemp, message] : withoutTemp };
    });
  }, []);

  const handleSend = async () => {
    const body = replyText.trim();
    if (!body || !selectedId) return;
    if (thread?.session_ended || thread?.can_send_text === false) return;
    if (!thread?.can_reply && !thread?.can_claim) return;
    if (sending) return;

    // Capture before awaits — switching conversations mid-send must not
    // replace temps / restore draft on the wrong thread. Compare against
    // selectedIdRef (not the closed-over selectedId) after each await.
    const conversationId = selectedId;
    const stillOnThread = () =>
      String(selectedIdRef.current || "") === String(conversationId);
    const canClaim = Boolean(thread?.can_claim);
    const canReply = Boolean(thread?.can_reply);

    const tempId = `local-${Date.now()}`;
    const optimistic = {
      id: tempId,
      direction: "outbound",
      body,
      sent_at: new Date().toISOString(),
      status: "pending",
      media_json: null,
      sender_name: null,
    };

    setReplyText("");
    appendOutboundMessage(optimistic, conversationId);
    setSending(true);
    try {
      if (!canReply && canClaim) {
        await axiosInstance.post(`/whatsapp/conversations/${conversationId}/claim`);
      }
      const { data } = await axiosInstance.post(
        `/whatsapp/conversations/${conversationId}/messages`,
        { body }
      );
      if (stillOnThread()) {
        replaceTempOutboundMessage(tempId, data, conversationId);
        void loadThread(conversationId, { quiet: true });
      }
      void loadInbox({ quiet: true });
    } catch (error) {
      if (stillOnThread()) {
        replaceTempOutboundMessage(tempId, null, conversationId);
        setReplyText(body);
        void loadThread(conversationId, { quiet: true });
      }
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not send message."),
      });
    } finally {
      setSending(false);
    }
  };

  const openTemplateDialog = () => {
    setPlusOpen(false);
    setSelectedTemplate(null);
    setTemplateVars([]);
    setTemplateDialogOpen(true);
    loadTemplates();
  };

  const closePlusMenu = () => setPlusOpen(false);

  const canSendFreeform =
    Boolean(thread?.can_reply || thread?.can_claim) &&
    !thread?.session_ended &&
    thread?.can_send_text !== false;

  const ensureClaimed = async () => {
    if (!thread?.can_reply && thread?.can_claim && selectedId) {
      await axiosInstance.post(`/whatsapp/conversations/${selectedId}/claim`);
    }
  };

  const sendMediaFile = async (file) => {
    if (!file || !selectedId) return;
    if (!canSendFreeform && !(thread?.can_reply || thread?.can_claim)) return;
    if (thread?.session_ended) {
      setToast({
        severity: "warning",
        message: "Session ended — only approved templates can be sent until the customer messages again.",
      });
      return;
    }
    setSendingMedia(true);
    try {
      await ensureClaimed();
      const form = new FormData();
      form.append("file", file, file.name || "attachment");
      await axiosInstance.post(
        `/whatsapp/conversations/${selectedId}/media-messages`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await loadThread(selectedId, { quiet: true });
      await loadInbox({ quiet: true });
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not send attachment."),
      });
    } finally {
      setSendingMedia(false);
    }
  };

  const handleAttachPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) await sendMediaFile(file);
  };

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const cancelRecording = () => {
    clearRecordingTimer();
    recordingConversationIdRef.current = null;
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
    mediaRecorderRef.current = null;
    mediaChunksRef.current = [];
    stopMediaTracks();
    setRecording(false);
    setRecordingSeconds(0);
  };

  const handleSelect = (conversationId) => {
    if (recording || mediaRecorderRef.current) {
      cancelRecording();
    }
    setSearchParams({ conversation: String(conversationId) });
  };

  const startRecording = async () => {
    if (!canSendFreeform || recording || sending || sendingMedia) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setToast({
        severity: "error",
        message: "Voice messages are not supported in this browser.",
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const preferredTypes = [
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/webm",
      ];
      const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaChunksRef.current = [];
      const conversationIdAtStart = selectedId;
      recordingConversationIdRef.current = conversationIdAtStart;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) mediaChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        clearRecordingTimer();
        stopMediaTracks();
        setRecording(false);
        const chunks = mediaChunksRef.current;
        mediaChunksRef.current = [];
        mediaRecorderRef.current = null;
        const startedFor = recordingConversationIdRef.current;
        recordingConversationIdRef.current = null;
        if (!chunks.length) {
          setRecordingSeconds(0);
          return;
        }
        if (
          !startedFor ||
          String(selectedIdRef.current || "") !== String(startedFor)
        ) {
          setRecordingSeconds(0);
          setToast({
            severity: "info",
            message: "Voice note discarded — conversation changed.",
          });
          return;
        }
        const blobType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunks, { type: blobType });
        const ext = blobType.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voice-note.${ext}`, { type: blobType.split(";")[0] });
        setRecordingSeconds(0);
        await sendMediaFile(file);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecording(true);
      setRecordingSeconds(0);
      clearRecordingTimer();
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch {
      stopMediaTracks();
      recordingConversationIdRef.current = null;
      setToast({
        severity: "error",
        message: "Microphone permission is required to record a voice message.",
      });
    }
  };

  const stopRecordingAndSend = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      cancelRecording();
      return;
    }
    try {
      recorder.stop();
    } catch {
      cancelRecording();
    }
  };

  useEffect(() => () => {
    cancelRecording();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateVars(Array.from({ length: template.variable_count || 0 }, () => ""));
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate || !selectedId) return;
    if (!thread?.can_reply && !thread?.can_claim) return;
    const expected = selectedTemplate.variable_count || 0;
    if (templateVars.slice(0, expected).some((v) => !String(v).trim())) {
      setToast({ severity: "error", message: "Fill every template variable before sending." });
      return;
    }
    setSendingTemplate(true);
    try {
      if (!thread?.can_reply && thread?.can_claim) {
        await axiosInstance.post(`/whatsapp/conversations/${selectedId}/claim`);
      }
      await axiosInstance.post(`/whatsapp/conversations/${selectedId}/template-messages`, {
        template_id: selectedTemplate.id,
        variables: templateVars.slice(0, expected),
      });
      setTemplateDialogOpen(false);
      setSelectedTemplate(null);
      await loadThread(selectedId, { quiet: true });
      await loadInbox({ quiet: true });
    } catch (error) {
      setToast({
        severity: "error",
        message: getApiErrorMessage(error, "Could not send template."),
      });
    } finally {
      setSendingTemplate(false);
    }
  };

  const totalUnread = useMemo(
    () => inbox.reduce((sum, item) => sum + (item.unread_count || 0), 0),
    [inbox]
  );
  const isV2 = useTheme()?.pearlUi === "v2";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        ref={pageRef}
        px={isV2 ? 0 : { xs: 1, sm: 2 }}
        pt={0.5}
        sx={{
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          minHeight: 0,
          overflow: "hidden",
          // Fallback is conservative so the send bar stays on screen before JS
          // measures. After measure, height === maxHeight so the panel cannot
          // grow past the visible viewport and clip the composer.
          height: pageHeight ? `${pageHeight}px` : "calc(100dvh - 160px)",
          maxHeight: pageHeight ? `${pageHeight}px` : "calc(100dvh - 160px)",
          [`@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`]: {
            height: pageHeight ? `${pageHeight}px` : scaledVh("220px"),
            maxHeight: pageHeight ? `${pageHeight}px` : scaledVh("220px"),
          },
        }}
      >
        {isV2 ? (
          // v2 (design-reference "Service · WhatsApp"): page title with the
          // unread count, refresh on the right. Back is the breadcrumb.
          <Box sx={{ flexShrink: 0 }}>
            <PageHeader
              title="Messages"
              summary={`WhatsApp${totalUnread > 0 ? ` · ${totalUnread} unread` : ""}${!wsConnected ? " · reconnecting…" : ""}`}
              actions={(
                <Tooltip title="Refresh">
                  <span>
                    <IconButton aria-label="Refresh" onClick={handleManualRefresh} disabled={refreshing} size="small">
                      {refreshing ? <CircularProgress size={20} /> : <Refresh />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            />
          </Box>
        ) : (
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1} flexShrink={0}>
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <IconButton onClick={() => navigate("/")} size="small">
              <ArrowBack />
            </IconButton>
            {/* <Box
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                borderRadius: "12px",
                bgcolor: WA_GREEN,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(37,211,102,0.35)",
                flexShrink: 0,
              }}
            >
              <WhatsApp sx={{ color: "#fff", fontSize: 26 }} />
            </Box> */}
            <Box>
              <MDTypography variant="h5" fontWeight="bold" lineHeight={1.2}>
                Messages
              </MDTypography>
              {/* <MDTypography variant="caption" color="text">
                {totalUnread > 0 ? `${totalUnread} unread` : "Inbox from your queues"}
                {!wsConnected ? " · reconnecting…" : ""}
              </MDTypography> */}
            </Box>
          </MDBox>
          <Tooltip title="Refresh">
            <IconButton onClick={handleManualRefresh} disabled={refreshing} size="small">
              {refreshing ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </MDBox>
        )}

        {inboxError ? (
          <Alert severity="warning" sx={{ mb: 1, flexShrink: 0 }}>
            {inboxError}
          </Alert>
        ) : null}

        <MDBox
          display="flex"
          flex={1}
          minHeight={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: isV2 ? "10px" : "16px",
            overflow: "hidden",
            bgcolor: "white",
            boxShadow: isV2 ? "none" : "0 4px 24px rgba(0,0,0,0.06)",
            ...(isV2 && { borderColor: t.border }),
          }}
        >
          {/* Inbox sidebar */}
          <MDBox
            sx={{
              width: { xs: "100%", md: 300, lg: 360 },
              flexShrink: 0,
              borderRight: { md: "1px solid" },
              borderColor: { md: "divider" },
              display: { xs: selectedId ? "none" : "flex", md: "flex" },
              flexDirection: "column",
              bgcolor: isV2 ? t.surface : "#fafafa",
              ...(isV2 && { borderColor: { md: t.border } }),
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              px={2}
              py={1.5}
              borderBottom="1px solid"
              borderColor="divider"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={isV2 ? { bgcolor: t.surface, borderColor: t.border, flexShrink: 0 } : { bgcolor: HEADER_BLUE_BG, flexShrink: 0 }}
            >
              <Typography variant="subtitle1" fontWeight={600}
                          sx={isV2 ? { fontSize: 15, color: t.text } : { color: HEADER_BLUE }}>
                Conversations
              </Typography>
              {inbox.length > 0 ? (
                <Chip
                  label={inbox.length}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    bgcolor: isV2 ? t.primary : HEADER_BLUE,
                    color: "#fff",
                  }}
                />
              ) : null}
            </Box>

            {loadingInbox ? (
              <MDBox display="flex" justifyContent="center" py={8}>
                <CircularProgress size={28} sx={{ color: WA_GREEN }} />
              </MDBox>
            ) : inbox.length > 0 ? (
              <List disablePadding sx={{ overflowY: "auto", flex: 1, ...THIN_SCROLLBAR }}>
                {inbox.map((item) => (
                  <InboxRow
                    key={item.conversation_id}
                    item={item}
                    selected={String(item.conversation_id) === selectedId}
                    onSelect={handleSelect}
                  />
                ))}
              </List>
            ) : inboxError ? null : (
              <MDBox px={3} py={8} textAlign="center">
                <WhatsApp sx={{ fontSize: 48, color: WA_GREEN, opacity: 0.5, mb: 2, ...(isV2 && { width: 40, height: 40 }) }} />
                <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                  No conversations yet. When customers message your WhatsApp number, threads
                  appear here from queues you belong to.
                </Typography>
              </MDBox>
            )}
          </MDBox>

          {/* Chat panel */}
          <MDBox
            flex={1}
            display={{ xs: selectedId ? "flex" : "none", md: "flex" }}
            flexDirection="column"
            minWidth={0}
            minHeight={0}
            overflow="hidden"
            bgcolor={isV2 ? t.bg : CHAT_BG}
            sx={isV2 ? undefined : {
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9ced6' fill-opacity='0.09'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            }}
          >
            {!selectedId ? (
              inboxError ? (
                <MDBox flex={1} />
              ) : (
              <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={3}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <WhatsApp sx={{ fontSize: 40, color: WA_GREEN, ...(isV2 && { width: 36, height: 36 }) }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
                  Select a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={320}>
                  Choose a thread from the left to read and reply to WhatsApp messages.
                </Typography>
              </MDBox>
              )
            ) : loadingThread ? (
              <MDBox flex={1} display="flex" alignItems="center" justifyContent="center" bgcolor="rgba(255,255,255,0.5)">
                <CircularProgress size={28} sx={{ color: WA_GREEN }} />
              </MDBox>
            ) : (
              <>
                {/* Chat header */}
                <Box
                  px={2}
                  py={1.25}
                  borderBottom="1px solid"
                  borderColor="divider"
                  bgcolor="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  boxShadow="0 1px 3px rgba(0,0,0,0.06)"
                  flexShrink={0}
                >
                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <IconButton sx={{ display: { md: "none" } }} onClick={() => setSearchParams({})} size="small">
                      <ArrowBack />
                    </IconButton>
                    <ContactAvatar size={42} />
                    <Box minWidth={0}>
                      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {thread?.contact_name || thread?.phone_number || "Conversation"}
                        </Typography>
                        {assignmentChip(thread?.assignment)}
                        <RelatedChips related={thread?.related} onOpen={openInNewTab} inline compact />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {thread?.phone_number}
                        {thread?.assigned_to_name ? ` · Assigned to ${thread.assigned_to_name}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                    {thread?.can_claim ? (
                      <MDButton
                        variant="gradient"
                        color="warning"
                        size="small"
                        onClick={handleClaim}
                        disabled={claiming}
                      >
                        {claiming ? <CircularProgress size={18} color="inherit" /> : "Claim"}
                      </MDButton>
                    ) : null}
                    {thread?.can_transfer || (thread?.can_reply && !thread?.session_ended) ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={openTransferDialog}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: HEADER_BLUE,
                          color: `${HEADER_BLUE} !important`,
                          bgcolor: "#fff",
                          "&:hover": { borderColor: HEADER_BLUE, bgcolor: HEADER_BLUE_BG },
                        }}
                      >
                        Assign to…
                      </Button>
                    ) : null}
                    {thread?.can_reply && !thread?.session_ended ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleEndSession}
                        disabled={endingSession}
                        sx={{
                          bgcolor: "#d32f2f",
                          color: "#fff !important",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#b71c1c" },
                          "&.Mui-disabled": { bgcolor: "rgba(211,47,47,0.4)", color: "#fff !important" },
                        }}
                      >
                        {endingSession ? <CircularProgress size={18} color="inherit" /> : "End session"}
                      </Button>
                    ) : null}
                  </Box>
                </Box>

                {/* Messages — scroll inside the panel only, not the whole page */}
                <Box
                  ref={messagesScrollRef}
                  flex={1}
                  minHeight={0}
                  px={{ xs: 1, sm: 2 }}
                  py={1.5}
                  sx={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    ...THIN_SCROLLBAR,
                  }}
                >
                  {(thread?.messages || []).length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="text.secondary">
                        No messages in this thread yet.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Grows only when the thread is shorter than the pane so
                          bubbles sit on the composer, not under the header. */}
                      <Box aria-hidden sx={{ flex: "1 0 0", minHeight: 0 }} />
                      <Box sx={{ flex: "0 0 auto" }}>
                        <MessageThread messages={thread?.messages} />
                      </Box>
                    </>
                  )}
                </Box>

                {/* Composer */}
                <Box
                  bgcolor="white"
                  borderTop="1px solid"
                  borderColor="divider"
                  p={{ xs: 1, sm: 1.25 }}
                  flexShrink={0}
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                  }}
                >
                  {thread?.can_reply || thread?.can_claim ? (
                    <>
                      {thread?.can_claim && !thread?.can_reply ? (
                        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                          Waiting in your queue — send a reply to claim, or use Claim first.
                        </Alert>
                      ) : null}
                      {thread?.session_ended ? (
                        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                          Session ended. Free-form messages are locked until the customer
                          sends a new message. You can still send an approved template.
                        </Alert>
                      ) : null}
                      <Box display="flex" gap={1} alignItems="flex-end">
                        <input
                          ref={attachInputRef}
                          type="file"
                          hidden
                          accept="image/jpeg,image/png,image/webp,audio/*,video/mp4,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                          onChange={handleAttachPick}
                        />
                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                          <Tooltip title="Attach, template, emoji">
                            <span>
                              <IconButton
                                ref={plusBtnRef}
                                onClick={() => setPlusOpen((open) => !open)}
                                disabled={sending || sendingMedia || recording}
                                sx={{
                                  bgcolor: WA_GREEN,
                                  color: "#fff",
                                  width: 42,
                                  height: 42,
                                  flexShrink: 0,
                                  "&:hover": { bgcolor: WA_GREEN_DARK },
                                  "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                                }}
                              >
                                <Add sx={{ fontSize: 24 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {plusOpen ? (
                            <ClickAwayListener onClickAway={closePlusMenu}>
                              <Paper
                                elevation={8}
                                // In-flow under body { zoom } — avoids portaled Menu drift
                                // when CRM size is 80% (navbar A / a controls).
                                sx={{
                                  position: "absolute",
                                  bottom: "calc(100% + 8px)",
                                  left: 0,
                                  zIndex: 20,
                                  minWidth: 260,
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  py: 0.5,
                                }}
                              >
                                <MenuItem
                                  disabled={Boolean(thread?.session_ended) || sendingMedia}
                                  onClick={() => {
                                    closePlusMenu();
                                    attachInputRef.current?.click();
                                  }}
                                >
                                  <ListItemIcon>
                                    <AttachFile fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary="Attachment" secondary="Photo, file, or audio" />
                                </MenuItem>
                                <MenuItem onClick={openTemplateDialog}>
                                  <ListItemIcon>
                                    <Description fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary="Templates" secondary="Approved WhatsApp templates" />
                                </MenuItem>
                                <MenuItem
                                  disabled={Boolean(thread?.session_ended)}
                                  onClick={() => {
                                    closePlusMenu();
                                    setEmojiAnchor(plusBtnRef.current);
                                  }}
                                >
                                  <ListItemIcon>
                                    <EmojiEmotions fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary="Emoji" />
                                </MenuItem>
                              </Paper>
                            </ClickAwayListener>
                          ) : null}
                        </Box>
                        {recording ? (
                          <Box
                            flex={1}
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            px={2}
                            py={1}
                            sx={{
                              borderRadius: "24px",
                              bgcolor: "#f5f5f5",
                              minHeight: 42,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "error.main",
                                animation: "waPulse 1s ease-in-out infinite",
                                "@keyframes waPulse": {
                                  "0%, 100%": { opacity: 1 },
                                  "50%": { opacity: 0.35 },
                                },
                              }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              Recording… {String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:
                              {String(recordingSeconds % 60).padStart(2, "0")}
                            </Typography>
                            <Button size="small" color="inherit" onClick={cancelRecording}>
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <TextField
                            fullWidth
                            multiline
                            maxRows={5}
                            inputRef={replyInputRef}
                            placeholder={
                              thread?.session_ended
                                ? "Waiting for customer message…"
                                : "Type a message…"
                            }
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                              }
                            }}
                            size="small"
                            disabled={Boolean(thread?.session_ended) || sendingMedia}
                            InputProps={{
                              sx: {
                                borderRadius: "24px",
                                bgcolor: "#f5f5f5",
                                "& fieldset": { border: "none" },
                              },
                            }}
                          />
                        )}
                        {recording ? (
                          <Tooltip title="Send voice message">
                            <IconButton
                              onClick={stopRecordingAndSend}
                              disabled={sendingMedia}
                              sx={{
                                bgcolor: WA_GREEN,
                                color: "#fff",
                                width: 42,
                                height: 42,
                                flexShrink: 0,
                                "&:hover": { bgcolor: WA_GREEN_DARK },
                              }}
                            >
                              {sendingMedia ? (
                                <CircularProgress size={22} color="inherit" />
                              ) : (
                                <Stop sx={{ fontSize: 22 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : replyText.trim() ? (
                          <IconButton
                            onClick={handleSend}
                            disabled={
                              sending ||
                              sendingMedia ||
                              !replyText.trim() ||
                              Boolean(thread?.session_ended)
                            }
                            sx={{
                              bgcolor: WA_GREEN,
                              color: "#fff",
                              width: 42,
                              height: 42,
                              flexShrink: 0,
                              "&:hover": { bgcolor: WA_GREEN_DARK },
                              "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                            }}
                          >
                            {sending ? (
                              <Send sx={{ fontSize: 20, opacity: 0.7 }} />
                            ) : (
                              <Send sx={{ fontSize: 20 }} />
                            )}
                          </IconButton>
                        ) : (
                          <Tooltip
                            title={
                              thread?.session_ended
                                ? "Voice notes need an open session"
                                : "Record voice message"
                            }
                          >
                            <span>
                              <IconButton
                                onClick={startRecording}
                                disabled={
                                  sending ||
                                  sendingMedia ||
                                  Boolean(thread?.session_ended) ||
                                  !canSendFreeform
                                }
                                sx={{
                                  bgcolor: WA_GREEN,
                                  color: "#fff",
                                  width: 42,
                                  height: 42,
                                  flexShrink: 0,
                                  "&:hover": { bgcolor: WA_GREEN_DARK },
                                  "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                                }}
                              >
                                {sendingMedia ? (
                                  <CircularProgress size={22} color="inherit" />
                                ) : (
                                  <Mic sx={{ fontSize: 22 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      <EmojiPicker
                        anchorEl={emojiAnchor}
                        onClose={() => setEmojiAnchor(null)}
                        onPick={handleEmojiPick}
                      />
                    </>
                  ) : (
                    <Alert severity="info" icon={false}>
                      You can view this thread, but only the assigned agent can reply.
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </MDBox>
        </MDBox>
      </MDBox>

      <Dialog
        open={templateDialogOpen}
        onClose={() => !sendingTemplate && setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send a WhatsApp template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Use a Meta-approved template to message this customer, including after
            the 24-hour window. Add templates under Settings → Company Settings →
            Templates → WhatsApp.
          </Typography>
          {templates.length === 0 ? (
            <Alert severity="info">
              No approved templates are enabled yet. Sync or add them in Company
              Settings → Templates → WhatsApp.
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1} mb={selectedTemplate ? 2 : 0}>
              {templates.map((template) => (
                <Box
                  key={template.id}
                  onClick={() => pickTemplate(template)}
                  sx={{
                    border: "1px solid",
                    borderColor: selectedTemplate?.id === template.id ? WA_GREEN_DARK : "divider",
                    bgcolor: selectedTemplate?.id === template.id ? "rgba(18,140,126,0.06)" : "white",
                    borderRadius: "10px",
                    p: 1.5,
                    cursor: "pointer",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatWhatsAppLanguage(template.language)}
                      sx={{ height: 20 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {template.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {selectedTemplate?.variable_count > 0 ? (
            <Box mt={1}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Fill placeholders
              </Typography>
              {Array.from({ length: selectedTemplate.variable_count }).map((_, idx) => (
                <TextField
                  key={`${selectedTemplate.id}-${idx}`}
                  fullWidth
                  size="small"
                  label={`{{${idx + 1}}}`}
                  value={templateVars[idx] || ""}
                  onChange={(e) => {
                    const next = [...templateVars];
                    next[idx] = e.target.value;
                    setTemplateVars(next);
                  }}
                  sx={{ mt: 1 }}
                />
              ))}
              <Box mt={1.5} p={1.5} bgcolor="#f5f5f5" borderRadius="8px">
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Preview
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {previewTemplateBody(selectedTemplate.body, templateVars)}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={sendingTemplate}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTemplate}
            disabled={sendingTemplate || !selectedTemplate}
            sx={{ bgcolor: WA_GREEN, color: "#fff", "&:hover": { bgcolor: WA_GREEN_DARK } }}
          >
            {sendingTemplate ? <CircularProgress size={18} color="inherit" /> : "Send template"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferOpen}
        onClose={() => !transferringTo && setTransferOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Assign to another agent</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Only agents in the same queue are listed. They will see the full chat history and linked
            records.
          </Typography>
          {loadingTransfer ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : transferCandidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No other agents are in this queue.
            </Typography>
          ) : (
            <List dense disablePadding>
              {transferCandidates.map((agent) => (
                <MenuItem
                  key={agent.user_id}
                  onClick={() => handleTransfer(agent.user_id)}
                  disabled={Boolean(transferringTo)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={agent.name}
                    secondary={agent.state ? `Presence: ${agent.state}` : "Presence unknown"}
                  />
                  {transferringTo === agent.user_id ? (
                    <CircularProgress size={18} />
                  ) : null}
                </MenuItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={Boolean(transferringTo)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {typeof toast.message === "string" ? toast.message : "Something went wrong."}
          </Alert>
        ) : null}
      </Snackbar>
    </DashboardLayout>
  );
}
